/**
 * PendingDraftsPage — /admin/pending-drafts
 *
 * iter150AZ.67 — unified dashboard for super-admin / service-admin /
 * sales-admin showing everything that's mid-flight in the system:
 *
 *   • Commissioning visits not yet submitted
 *   • PM service visits not yet submitted
 *   • Warranty claims still in draft
 *   • Quote drafts (legacy MySQL Quote_Heading rows)
 *   • Live offline-queue heartbeats from each tech's device
 *
 * Visibility split is decided server-side (see pending_drafts_routes.py):
 *   - super_admin sees everything (4 buckets + heartbeats)
 *   - service admins see service-side records only
 *   - sales admins see Quote drafts only
 *
 * Auto-refreshes every 60 s while the tab is visible so admins get a
 * near-live picture of what each tech has captured but not yet shipped.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowsClockwise, Funnel, Tray, MagnifyingGlass,
  Cube, FirstAidKit, ShieldCheck, ReceiptX, Person, ClockClockwise,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { HelpPill } from "@/components/HeaderBrand";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";
import SortableTh, { nextSort, compareBy } from "@/components/SortableTh";

const KIND_META = {
  commissioning: { label: "Commissioning",  icon: Cube,        cls: "bg-violet-50 text-violet-800 border-violet-200" },
  pm_service:    { label: "PM Service",      icon: FirstAidKit, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  warranty:      { label: "Warranty",        icon: ShieldCheck, cls: "bg-rose-50 text-rose-800 border-rose-200" },
  quote:         { label: "Quote Draft",     icon: ReceiptX,    cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

function _formatAge(sec) {
  if (!sec || sec <= 0) return "—";
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function _ageBadgeCls(sec) {
  // 30m → green; 30m–4h → amber; 4h+ → rose
  if (sec < 1800)   return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (sec < 14400)  return "text-amber-800 bg-amber-50 border-amber-200";
  return "text-rose-800 bg-rose-50 border-rose-200";
}

export default function PendingDraftsPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeKinds, setActiveKinds] = useState(new Set(["commissioning","pm_service","warranty","quote"]));
  const [techFilter, setTechFilter] = useState("");
  const [sort, setSort] = useState({ key: "age_sec", dir: "desc" });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pending-drafts");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load pending drafts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  // Auto-refresh every 60 s while the tab is visible. Pauses when the
  // tab is hidden so we don't burn API quota on background tabs.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  // Flatten all buckets into a single rows array so the table can sort
  // & filter across kinds.
  const allRows = useMemo(() => {
    if (!data?.buckets) return [];
    const rows = [];
    for (const k of Object.keys(data.buckets)) {
      for (const r of data.buckets[k] || []) rows.push({ ...r, kind: r.kind || k });
    }
    return rows;
  }, [data]);

  const filtered = useMemo(() => {
    let rows = allRows.filter(r => activeKinds.has(r.kind));
    if (techFilter.trim()) {
      const q = techFilter.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.tech_email || "").toLowerCase().includes(q)
        || (r.company_name || "").toLowerCase().includes(q)
        || (r.serial_number || "").toLowerCase().includes(q)
      );
    }
    if (!sort.key) return rows;
    const cmp = (a, b) => {
      const r = compareBy(a, b, sort.key);
      return sort.dir === "desc" ? -r : r;
    };
    return [...rows].sort(cmp);
  }, [allRows, activeKinds, techFilter, sort]);

  const totals = data?.totals || {};
  const viewer = data?.viewer || {};
  const visibleKinds = useMemo(() => {
    // Service admin without sales sees no Quote chip; sales-only sees
    // no Service chips. Super-admin sees all four.
    const out = [];
    if (viewer.is_super || viewer.can_service) {
      out.push("commissioning", "pm_service", "warranty");
    }
    if (viewer.is_super || viewer.can_sales) {
      out.push("quote");
    }
    return out;
  }, [viewer]);

  const toggleKind = (k) => {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-3">
        <button
          data-testid="pending-drafts-back"
          onClick={() => navigate(-1)}
          className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-600"
          title="Back"
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        <div className="flex items-center gap-2">
          <Tray size={18} weight="fill" className="text-amber-700" />
          <h1 className="text-base font-black tracking-tighter font-['Chivo']">
            {t("pending_drafts.title", "Pending Drafts")}
          </h1>
        </div>
        <button
          data-testid="pending-drafts-refresh"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-7 px-2 text-[11px] font-semibold rounded-sm border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          title={t("pending_drafts.refresh_tip", "Refresh")}
        >
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          {t("pending_drafts.refresh", "Refresh")}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <HelpPill />
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Stat tiles */}
        <section data-testid="pending-drafts-totals" className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {visibleKinds.map((k) => {
            const meta = KIND_META[k];
            const Icon = meta.icon;
            const n = totals[k] || 0;
            const active = activeKinds.has(k);
            return (
              <button
                key={k}
                data-testid={`pending-drafts-kind-${k}`}
                data-active={active ? "1" : "0"}
                onClick={() => toggleKind(k)}
                className={`text-left p-3 rounded-sm border transition-colors ${
                  active ? meta.cls + " ring-2 ring-offset-1 ring-slate-300"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                  <Icon size={11} weight="bold" />
                  {meta.label}
                </div>
                <div className="text-2xl font-black mt-1 font-['Chivo']">{n}</div>
              </button>
            );
          })}
          <div className="p-3 rounded-sm border border-slate-200 bg-white">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
              <ClockClockwise size={11} weight="bold" />
              {t("pending_drafts.heartbeats_label", "Live device queues")}
            </div>
            <div className="text-2xl font-black mt-1 font-['Chivo']" data-testid="pending-drafts-heartbeats-count">
              {totals.heartbeats || 0}
            </div>
          </div>
        </section>

        {/* Heartbeats (one row per tech with queued work on their phone) */}
        {(data?.heartbeats || []).length > 0 && (
          <section data-testid="pending-drafts-heartbeats" className="bg-white border border-slate-200 rounded-sm p-3">
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
              <Tray size={12} weight="bold" /> {t("pending_drafts.heartbeats_section", "Work waiting on field devices")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {data.heartbeats.map((h, i) => (
                <div key={i} data-testid={`pending-drafts-hb-${i}`}
                     className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-[12px] font-mono">
                  <Person size={12} weight="bold" />
                  <span className="font-bold">{h.email}</span>
                  <span className="text-amber-700">·</span>
                  <span>{h.depth} {h.depth === 1 ? "draft" : "drafts"}</span>
                  <span className="text-amber-700">·</span>
                  <span className="text-[10px]">{_formatAge(h.age_sec)} ago</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Search */}
        <section className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute top-2.5 left-2 text-slate-400" />
            <input
              data-testid="pending-drafts-search"
              type="text"
              placeholder={t("pending_drafts.search_ph", "Filter by tech email, company, or serial #")}
              value={techFilter}
              onChange={(e) => setTechFilter(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            />
          </div>
          <div className="ml-auto text-[11px] text-slate-500 font-mono">
            {filtered.length} / {allRows.length} {t("pending_drafts.rows_label", "rows")}
          </div>
        </section>

        {/* Table */}
        <section className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="pending-drafts-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600 w-[110px]">
                    {t("pending_drafts.col_type", "Type")}
                  </th>
                  <SortableTh column="company_name" sortKey={sort.key} sortDir={sort.dir}
                              onSort={(k) => setSort(nextSort(sort.key, sort.dir, k))}
                              className="text-left px-2 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">
                    {t("pending_drafts.col_customer","Customer")}
                  </SortableTh>
                  <SortableTh column="title" sortKey={sort.key} sortDir={sort.dir}
                              onSort={(k) => setSort(nextSort(sort.key, sort.dir, k))}
                              className="text-left px-2 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">
                    {t("pending_drafts.col_item","Item")}
                  </SortableTh>
                  <SortableTh column="tech_email" sortKey={sort.key} sortDir={sort.dir}
                              onSort={(k) => setSort(nextSort(sort.key, sort.dir, k))}
                              className="text-left px-2 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">
                    {t("pending_drafts.col_tech","By whom")}
                  </SortableTh>
                  <SortableTh column="age_sec" sortKey={sort.key} sortDir={sort.dir}
                              onSort={(k) => setSort(nextSort(sort.key, sort.dir, k))}
                              className="text-left px-2 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600 w-[80px]">
                    {t("pending_drafts.col_age","Age")}
                  </SortableTh>
                  <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">
                    {t("pending_drafts.col_step", "Step")}
                  </th>
                  <th className="px-2 py-2 w-[80px]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400 text-sm" data-testid="pending-drafts-empty">
                    {loading ? t("pending_drafts.loading", "Loading…") : t("pending_drafts.empty", "Nothing pending. The team is all caught up. ✓")}
                  </td></tr>
                ) : filtered.map((r) => {
                  const meta = KIND_META[r.kind] || KIND_META.commissioning;
                  const Icon = meta.icon;
                  return (
                    <tr key={`${r.kind}-${r.id}`}
                        data-testid={`pending-drafts-row-${r.id}`}
                        className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
                          <Icon size={10} weight="bold" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-slate-800 max-w-[240px] truncate" title={r.company_name}>
                        {r.company_name || "—"}
                      </td>
                      <td className="px-2 py-2 text-slate-800">
                        <div className="font-mono text-[11px]">{r.serial_number || "—"}</div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[260px]">{r.title}</div>
                      </td>
                      <td className="px-2 py-2 text-slate-700 font-mono text-[11px] truncate max-w-[180px]" title={r.tech_email}>
                        {r.tech_email || "—"}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-mono font-bold ${_ageBadgeCls(r.age_sec)}`}>
                          {_formatAge(r.age_sec)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-slate-600 text-[11px]">
                        {r.current_step || "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          data-testid={`pending-drafts-open-${r.id}`}
                          onClick={() => navigate(r.open_url)}
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          {t("pending_drafts.open", "Open")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <p className="text-[10px] text-slate-400 text-center pt-2">
          {t("pending_drafts.footer", "Auto-refreshes every 60 seconds. Drafts older than 60 days are hidden.")}
        </p>
      </main>
    </div>
  );
}
